import { useMemo } from 'react';
import {
  qAppConfig,
  getMainModuleConfig,
  isClinicModuleEnabled,
  type AppConfigRow,
  type ClinicMainModuleConfig,
  type ClinicMainModuleKey,
} from '@helios/data-access';
import { usePowerSyncQuery } from '../adapters/powersync';

/**
 * PRD.283 — reactive read of the clinic's main-module configuration from the
 * singleton app_config row (extra_json.main_modules). This is the single place
 * Clinic Settings, Consultation and Queue read the value from, so raw JSON is
 * never parsed independently.
 *
 * The setting is RESTRICTIVE ONLY: a missing value means enabled, so existing
 * clinics keep every module without a migration. Platform feature flags and
 * user permissions remain authoritative above this preference.
 */
export function useClinicMainModules(): {
  config: ClinicMainModuleConfig;
  isModuleEnabled: (module: ClinicMainModuleKey) => boolean;
} {
  const { data } = usePowerSyncQuery<AppConfigRow>(qAppConfig());
  return useMemo(() => {
    const config = getMainModuleConfig(data?.[0] ?? null);
    return {
      config,
      isModuleEnabled: (module: ClinicMainModuleKey) => isClinicModuleEnabled(config, module),
    };
  }, [data]);
}

/** Convenience: reactive boolean for the Invoice module (defaults to enabled). */
export function useInvoiceModuleEnabled(): boolean {
  const { isModuleEnabled } = useClinicMainModules();
  return isModuleEnabled('invoice');
}

/**
 * PRD.305 — reactive boolean for the Laboratory module (defaults to enabled).
 *
 * Disabling it hides EVERY Labs surface: the launcher tiles, the Laboratory
 * module nav, the patient sidebar Labs tab, and the Visit Note Labs section.
 * The related Patient Workspace settings toggles are forced off and read-only
 * so they cannot promise a tab the app no longer renders.
 *
 * This supersedes PRD.305 D5, which originally kept Consultation showing
 * published results as clinical history while the workflow was off. Prior
 * results remain reachable through Files & Images.
 */
export function useLaboratoryModuleEnabled(): boolean {
  const { isModuleEnabled } = useClinicMainModules();
  return isModuleEnabled('laboratory');
}
